/**
 * Group Info tab — read-only summary of the linked Group's key fields, plus the
 * group's primary actions (Members, Edit Group) condensed in one place.
 *
 * Shows the Group's VBA status, member counts, and surfaces the Members list and
 * the Edit Group modal inline. Laid out as sectioned cards with icon-badge
 * headers and two-column label/value grids.
 */

import React from 'react';
import {Link} from 'react-router-dom';
import {Building2, Link2, Database, Users, Edit, Loader2} from 'lucide-react';
import {STALE_TIME_5_MIN} from '@common/constants/time';
import {Button} from '@common-web/components/ui/button';
import {Card} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {cn} from '@common-web/lib/utils';
import {trpc} from '@common-web/lib/trpc';
import {buildGroupMemberSearchPath} from '@common-web/hooks/useMemberSearchUrlState';
import {useAuthStore} from '@common-web/stores/authStore';
import {toOpenEnrollmentWindow, type OpenEnrollmentMetadata} from '@common/lib/openEnrollment';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {CODE_PERMISSIONS} from '@common/permissions';
import {LinkVbaGroupModal} from '../components/LinkVbaGroupModal';
import {OpenEnrollmentCard} from '../components/OpenEnrollmentCard';
import {UpdateGroupModal} from '../../client/UpdateGroupModal';

interface GroupInfoTabProps {
  accountsId: string;
  companyName: string;
  // Null for accounts created via the UI that haven't been tied to a group yet —
  // the tab then shows the "Link existing VBA group" CTA so an admin can adopt a
  // group CBS already set up in VBA.
  group: {
    id: string;
    name: string;
    groupType: string;
    groupStatus: string;
    vbaGroupId: string | null;
    vbaDatabase: string | null;
    estimatedMemberCount: number | null;
    estimatedActiveMemberCount: number | null;
    createdAt: Date | string;
    vbaSyncedAt: Date | string | null;
    lastVBAError: string | null;
    metadata?: OpenEnrollmentMetadata | null;
  } | null;
  // Whether the caller may link a VBA group (client admin + tenant opt-in flag).
  // Computed by the parent so tab visibility and the in-tab CTA stay in sync.
  canLinkVbaGroup: boolean;
  // Whether the caller may manage the open enrollment window (client admin or
  // internal admin), mirroring `isAccountsAdmin` on the server.
  canEditOpenEnrollment: boolean;
}

function formatDate(d: Date | string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleString();
}

export function GroupInfoTab({
  accountsId,
  companyName,
  group,
  canLinkVbaGroup,
  canEditOpenEnrollment,
}: GroupInfoTabProps): React.ReactElement {
  const utils = trpc.useUtils();
  const isGroupUser = useAuthStore((state) => state.isGroupUser());
  const isInternal = useIsInternal();
  const canEditGroupFields = useHasPermission(CODE_PERMISSIONS.groups.edit.key);
  const canEditGroup = !isGroupUser && (!isInternal || canEditGroupFields);
  const [linkModalOpen, setLinkModalOpen] = React.useState(false);
  const [editModalOpen, setEditModalOpen] = React.useState(false);

  // Group edits write to VBA, so the modal needs the full VBA group record.
  // Fetched lazily when the user opens the Edit Group modal.
  const vbaGroupId = group?.vbaGroupId ?? null;
  const groupDetails = trpc.vba.groups.groupDetails.getDetails.useQuery(
    {groupId: vbaGroupId ?? ''},
    {
      enabled: editModalOpen && !!vbaGroupId,
      staleTime: STALE_TIME_5_MIN,
      refetchOnWindowFocus: false,
    },
  );

  const linkModal = (
    <LinkVbaGroupModal
      open={linkModalOpen}
      onOpenChange={setLinkModalOpen}
      accountsId={accountsId}
      defaultSearch={companyName}
      onLinked={() => {
        utils.account.getById.invalidate({accountsId});
        utils.account.list.invalidate();
      }}
    />
  );

  // No group yet (UI-created account) — show the adopt-an-existing-VBA-group CTA.
  if (!group) {
    return (
      <Card className='p-6'>
        <SectionHeader
          title='Group Info'
          icon={<Building2 className='h-[18px] w-[18px] text-blue-600' />}
          iconClassName='bg-blue-100'
        />
        <p className='text-sm text-muted-foreground'>
          This account isn&apos;t tied to a group yet.
          {canLinkVbaGroup
            ? ' Link it to a live group CBS already set up in VBA so the data shows together in this account.'
            : ''}
        </p>
        {canLinkVbaGroup ? (
          <Button
            variant='outline'
            size='sm'
            className='mt-4'
            onClick={() => setLinkModalOpen(true)}
          >
            <Link2 className='w-3 h-3 mr-1' />
            Link existing VBA group
          </Button>
        ) : null}
        {linkModal}
      </Card>
    );
  }

  const isLinked = !!group.vbaGroupId;

  return (
    <div className='space-y-5'>
      {/* Action bar */}
      <div className='flex items-center justify-between gap-4'>
        <div className='flex items-center gap-2'>
          {isLinked ? (
            <Badge variant='secondary' className='gap-1'>
              <Link2 className='h-3 w-3' />
              Linked to VBA
            </Badge>
          ) : null}
        </div>
        <div className='flex items-center gap-2'>
          {!isLinked && canLinkVbaGroup ? (
            <Button variant='outline' size='sm' onClick={() => setLinkModalOpen(true)}>
              <Link2 className='w-3 h-3 mr-1' />
              Link existing VBA group
            </Button>
          ) : null}
          {/* Deep-link into Member Search pre-filtered to this group so members
              are managed from one place (search, export, add member). */}
          <Link to={buildGroupMemberSearchPath(group.vbaGroupId ?? group.id, group.name)}>
            <Button variant='outline' size='sm'>
              <Users className='w-3 h-3 mr-1' />
              Members
            </Button>
          </Link>
          {isLinked && canEditGroup ? (
            <Button
              variant='outline'
              size='sm'
              onClick={() => setEditModalOpen(true)}
              disabled={editModalOpen && groupDetails.isLoading}
            >
              {editModalOpen && groupDetails.isLoading ? (
                <Loader2 className='w-3 h-3 mr-1 animate-spin' />
              ) : (
                <Edit className='w-3 h-3 mr-1' />
              )}
              Edit Group
            </Button>
          ) : null}
        </div>
      </div>

      {/* Group overview */}
      <Card className='p-6'>
        <SectionHeader
          title='Group Overview'
          icon={<Building2 className='h-[18px] w-[18px] text-blue-600' />}
          iconClassName='bg-blue-100'
        />
        <div className='grid grid-cols-2 gap-x-8 gap-y-5'>
          <Field label='Name' value={group.name} />
          <Field
            label='Status'
            value={
              <Badge variant={group.groupStatus === 'ACTIVE' ? 'success' : 'secondary'}>
                {group.groupStatus}
              </Badge>
            }
          />
          <Field
            label='Members'
            value={
              group.estimatedMemberCount !== null
                ? `${group.estimatedMemberCount} estimated · ${
                    group.estimatedActiveMemberCount ?? 0
                  } active`
                : null
            }
          />
          <Field label='Created' value={formatDate(group.createdAt)} />
        </div>
      </Card>

      {/* Open enrollment window — drives the green badge in the account header */}
      <OpenEnrollmentCard
        accountsId={accountsId}
        enrollmentWindow={toOpenEnrollmentWindow(group.metadata)}
        canEdit={canEditOpenEnrollment}
      />

      {/* VBA integration */}
      <Card className='p-6'>
        <SectionHeader
          title='VBA Integration'
          icon={<Database className='h-[18px] w-[18px] text-violet-600' />}
          iconClassName='bg-violet-100'
        />
        <div className='grid grid-cols-2 gap-x-8 gap-y-5'>
          <Field label='VBA Group ID' value={group.vbaGroupId} />
          {group.lastVBAError ? (
            <Field
              label='Last VBA Error'
              value={<span className='text-red-600'>{group.lastVBAError}</span>}
            />
          ) : null}
        </div>

        {!isLinked && canLinkVbaGroup ? (
          <p className='mt-5 text-xs text-muted-foreground'>
            Was this group already set up in VBA by CBS? Use{' '}
            <span className='font-medium'>Link existing VBA group</span> to connect it so all of the
            data shows together.
          </p>
        ) : null}
      </Card>

      {linkModal}

      {editModalOpen && groupDetails.data ? (
        <UpdateGroupModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          groupData={{
            groupId: groupDetails.data.groupId,
            name: groupDetails.data.name,
            federalId: groupDetails.data.federalId,
            originalDate: groupDetails.data.originalDate,
            disenrollDate: groupDetails.data.disenrollDate,
            renewalDate: null,
            billingCycle: groupDetails.data.billingCycle,
            address: groupDetails.data.address,
            primaryContact: groupDetails.data.primaryContact,
          }}
          onSuccess={() => {
            utils.account.getById.invalidate({accountsId});
          }}
        />
      ) : null}
    </div>
  );
}

function SectionHeader({
  title,
  icon,
  iconClassName,
}: {
  title: string;
  icon: React.ReactNode;
  iconClassName?: string;
}): React.ReactElement {
  return (
    <div className='flex items-center gap-3 mb-5'>
      <div
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
          iconClassName,
        )}
      >
        {icon}
      </div>
      <h3 className='text-base font-bold text-foreground'>{title}</h3>
    </div>
  );
}

function Field({label, value}: {label: string; value: React.ReactNode}): React.ReactElement {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div>
      <p className='mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        {label}
      </p>
      <div className='text-sm text-foreground'>{isEmpty ? '—' : value}</div>
    </div>
  );
}
